import React from 'react';
import {
  filterContractsByExpiration,
  calculateAvgGain,
  formatPercentage,
  formatNumber,
  formatDateShort
} from '../../utils/screenerDataHelpers';
import '../../styles/MobileScreener.css';

/**
 * Grid of bubbles - supports both grouped (by ticker) and individual (per contract) modes
 * Grouped: One bubble per ticker showing average gain and contract count
 * Individual: One bubble per contract showing ticker, strike, expiration, and gain
 */
const TickerStoriesGrid = ({
  resultsGrouped,
  selectedExpirations,
  onTickerClick,
  isGrouped = true,
  sortBy = 'gain'
}) => {
  // Filter contracts by selected expirations
  const getFilteredContracts = (contracts) => {
    return filterContractsByExpiration(contracts, selectedExpirations);
  };

  // Calculate average gain for ticker's filtered contracts
  const getAvgGain = (contracts) => {
    const filtered = getFilteredContracts(contracts);
    return calculateAvgGain(filtered);
  };

  // Render grouped mode (one bubble per ticker)
  const renderGroupedBubbles = () => {
    // Get all tickers with their average gains for sorting
    const tickersWithGains = Object.keys(resultsGrouped).map(ticker => {
      const contracts = resultsGrouped[ticker];
      const filteredContracts = getFilteredContracts(contracts);
      const avgGain = getAvgGain(contracts);
      return { ticker, avgGain, filteredContracts };
    }).filter(item => item.filteredContracts.length > 0);

    // Sort based on sortBy
    if (sortBy === 'gain') {
      tickersWithGains.sort((a, b) => b.avgGain - a.avgGain); // Highest gain first
    } else if (sortBy === 'expiration') {
      // Sort by earliest expiration date of contracts within each ticker
      tickersWithGains.sort((a, b) => {
        const expA = a.filteredContracts[0]?.Expiration || a.filteredContracts[0]?.expiration || '';
        const expB = b.filteredContracts[0]?.Expiration || b.filteredContracts[0]?.expiration || '';
        return expA.localeCompare(expB);
      });
    } else {
      // Default: alphabetical by ticker
      tickersWithGains.sort((a, b) => a.ticker.localeCompare(b.ticker));
    }

    return (
      <div className="ticker-stories-grid">
        {tickersWithGains.map(({ ticker, avgGain, filteredContracts }) => {
          const gainClass = avgGain >= 0 ? 'positive' : 'negative';
          const gainText = formatPercentage(avgGain);
          const ringClass = avgGain >= 0 ? 'positive' : 'negative';

          return (
            <div
              key={ticker}
              className="ticker-bubble"
              onClick={() => onTickerClick(ticker)}
            >
              <div className={`bubble-ring ${ringClass}`}>
                <div className="bubble-content">{ticker}</div>
              </div>
              <div className={`bubble-value ${gainClass}`}>{gainText}</div>
              <div className="bubble-label">
                {formatNumber(filteredContracts.length)} {filteredContracts.length === 1 ? 'contract' : 'contracts'}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render individual mode (one bubble per contract)
  const renderIndividualBubbles = () => {
    // Flatten all contracts into a single array
    const allContracts = [];
    Object.entries(resultsGrouped).forEach(([ticker, contracts]) => {
      const filtered = getFilteredContracts(contracts);
      filtered.forEach(contract => {
        allContracts.push({ ...contract, ticker });
      });
    });

    // Sort based on sortBy
    if (sortBy === 'gain') {
      allContracts.sort((a, b) => {
        const gainA = a['Premium % Gain'] || a.premiumGain || 0;
        const gainB = b['Premium % Gain'] || b.premiumGain || 0;
        return gainB - gainA; // Highest gain first
      });
    } else if (sortBy === 'expiration') {
      allContracts.sort((a, b) => {
        const expA = a.Expiration || a.expiration || '';
        const expB = b.Expiration || b.expiration || '';
        if (expA !== expB) return expA.localeCompare(expB);
        // If same expiration, sort by ticker then strike
        if (a.ticker !== b.ticker) return a.ticker.localeCompare(b.ticker);
        return (a.Strike || a.strike || 0) - (b.Strike || b.strike || 0);
      });
    }

    return (
      <div className="ticker-stories-grid ungrouped">
        {allContracts.map((contract, idx) => {
          const ticker = contract.ticker || contract.Ticker;
          const strike = contract.Strike || contract.strike;
          const expiration = contract.Expiration || contract.expiration;
          const gain = contract['Premium % Gain'] || contract.premiumGain || 0;
          const gainClass = gain >= 0 ? 'positive' : 'negative';

          return (
            <div
              key={`${ticker}-${strike}-${expiration}-${idx}`}
              className="ticker-bubble contract-bubble"
              onClick={() => onTickerClick(ticker)}
            >
              <div className={`bubble-ring ${gainClass}`}>
                <div className="bubble-content contract-bubble-content">
                  <span className="contract-ticker">{ticker}</span>
                  <span className="contract-strike">${strike}</span>
                </div>
              </div>
              <div className={`bubble-value ${gainClass}`}>{formatPercentage(gain)}</div>
              <div className="bubble-label">{formatDateShort(expiration)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // Check if there are any results
  const hasResults = Object.keys(resultsGrouped).length > 0;

  return (
    <div className="ticker-stories-container">
      {isGrouped ? renderGroupedBubbles() : renderIndividualBubbles()}

      {!hasResults && (
        <div className="mobile-loading-container">
          <div className="mobile-loading-text">No results to display</div>
        </div>
      )}
    </div>
  );
};

export default TickerStoriesGrid;
